import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import GoogleSignInButton from './GoogleSignInButton';
import pendingActionService from '../services/pendingActionService';
import { trackLandingView, trackCTAClick, trackSignupStart, trackSignupComplete, trackLogin } from '../utils/analytics';
import { getPostAuthRedirect } from '../utils/authRedirect';
import SEO from './SEO';
import PersonaCrossSell from './PersonaCrossSell';
import { PAGE_SEO, buildBreadcrumbs } from '../utils/seoConfig';
import './PersonaLanding.css';
import './MealPlanningLanding.css';

const SAMPLE_MENUS = [
  { id: 'menu_001', title: 'Grilled Chicken Quinoa Bowl', image: 'https://storage.googleapis.com/freshmenulab/menu_images/menu_001.jpg', cuisine: 'Mediterranean' },
  { id: 'menu_002', title: 'Vegan Buddha Bowl', image: 'https://storage.googleapis.com/freshmenulab/menu_images/menu_002.jpg', cuisine: 'International' },
  { id: 'menu_004', title: 'Vegetarian Lentil Curry', image: 'https://storage.googleapis.com/freshmenulab/menu_images/menu_004.jpg', cuisine: 'Indian' },
  { id: 'menu_005', title: 'Keto Steak Salad', image: 'https://storage.googleapis.com/freshmenulab/menu_images/menu_005.jpg', cuisine: 'American' },
  { id: 'menu_006', title: 'Thai Green Curry', image: 'https://storage.googleapis.com/freshmenulab/menu_images/menu_006.jpg', cuisine: 'Thai' },
  { id: 'menu_008', title: 'Korean BBQ Bowl', image: 'https://storage.googleapis.com/freshmenulab/menu_images/menu_008.jpg', cuisine: 'Korean' },
];

const MealPlanningLanding = () => {
  const navigate = useNavigate();
  const [authError, setAuthError] = useState(null);
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCardIndex((prev) => (prev + 1) % SAMPLE_MENUS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { trackLandingView('meal_planning'); }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    trackSignupStart('meal_planning');
    try {
      if (!credentialResponse.credential) { setAuthError('Missing authentication credentials'); return; }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential }),
      });
      if (!response.ok) throw new Error(`Authentication failed: ${response.status}`);
      const data = await response.json();
      const userData = { ...data.user, sub: data.user.sub || data.user.id || data.user.email };
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', data.token);
      if (data.isNewUser) {
        trackSignupComplete('meal_planning', userData.sub || userData.email);
      } else {
        trackLogin('meal_planning', userData.sub || userData.email);
      }
      const redirectUrl = await pendingActionService.checkAndExecutePendingAction();
      const next = getPostAuthRedirect({
        isNewUser: data.isNewUser,
        pendingRedirectUrl: redirectUrl,
        pendingSubscription: localStorage.getItem('pendingSubscription'),
        defaultHref: '/meal-plan'
      });
      if (redirectUrl) toast.success('Welcome!');
      window.location.href = next;
    } catch (error) {
      console.error('Auth error:', error);
      setAuthError(`Login failed: ${error.message}`);
    }
  };

  const seo = PAGE_SEO['/meal-planning'];

  return (
    <div className="persona-landing meal-planning">
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical="/meal-planning"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: seo.title,
            description: seo.description,
            url: 'https://freshfitlab.io/meal-planning',
            primaryImageOfPage: 'https://freshfitlab.io/og-image.jpg',
            about: {
              '@type': 'Thing',
              name: 'Meal Planning',
            },
          },
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'Meal Planning', url: '/meal-planning' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How does FreshFitLab meal planning work?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Browse 500+ recipes, drag the ones you want into your weekly meal plan, and the shopping list builds automatically. One tap sends the entire grocery list to Instacart.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is meal planning on FreshFitLab free?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. Browsing recipes, building meal plans, and exporting shopping lists to Instacart is free. Premium plans add AI generation, more saved menus, and additional capabilities.',
                },
              },
              {
                '@type': 'Question',
                name: 'Can I filter recipes by diet?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes — filter by vegetarian, vegan, gluten-free, keto, paleo, low-carb, high-protein, and more, plus by cuisine, prep time, and macros.',
                },
              },
            ],
          },
        ]}
      />
      <div className="pl-bg-shapes">
        <div className="pl-shape pl-shape-1"></div>
        <div className="pl-shape pl-shape-2"></div>
        <div className="pl-shape pl-shape-3"></div>
      </div>

      {/* ─── Hero ─── */}
      <section className="pl-hero">
        <span className="pl-hero-badge">Meal Planning Made Easy</span>
        <h1>
          Plan Your Meals,<br />
          <span className="pl-highlight">Shop in One Click</span>
        </h1>
        <p className="pl-hero-subtitle">
          Browse hundreds of recipes, build a personalized weekly menu, and send your entire grocery list
          to Instacart — all without leaving the app.
        </p>

        <div className="pl-hero-ctas">
          <button className="pl-cta-primary" onClick={() => { trackCTAClick('meal_planning', 'hero'); navigate('/menu'); }}>
            <span>Explore Menus</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
          <button className="pl-cta-secondary" onClick={() => { trackCTAClick('meal_planning', 'hero_secondary'); navigate('/meal-plan'); }}>
            <span>Build a Meal Plan</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>

        <Link
          to="/pricing#pricing-personal"
          className="pl-pricing-link pl-hero-pricing-link"
          onClick={() => trackCTAClick('meal_planning', 'hero_pricing')}
        >
          Free to start — plans from $5/month →
        </Link>

        <div className="pl-login-divider"><span>or</span></div>
        <div className="pl-login-section">
          <p className="pl-login-label">Sign in to get started</p>
          {process.env.NODE_ENV === 'development' ? (
            <button onClick={() => navigate('/dev/login')} className="pl-dev-login-button">Dev Login</button>
          ) : (
            <GoogleSignInButton onSuccess={handleGoogleSuccess} onError={() => setAuthError('Login Failed')} text="signin" shape="pill" />
          )}
          {authError && <p className="pl-auth-error">{authError}</p>}
        </div>

        <div className="pl-app-badges">
          <a href="https://apps.apple.com/us/app/freshmenulab/id6758483247" className="pl-app-badge" target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store">
            <svg viewBox="0 0 384 512" width="18" height="18" fill="currentColor"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
            <div className="pl-badge-text"><span className="pl-badge-small">Download on the</span><span className="pl-badge-large">App Store</span></div>
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.freshmenulab" className="pl-app-badge" target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play">
            <svg viewBox="0 0 512 512" width="18" height="18" fill="currentColor"><path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/></svg>
            <div className="pl-badge-text"><span className="pl-badge-small">Get it on</span><span className="pl-badge-large">Google Play</span></div>
          </a>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="pl-steps">
        <h2 className="pl-steps-title">How It Works</h2>
        <div className="pl-steps-grid">
          <div className="pl-step">
            <div className="pl-step-number">1</div>
            <h3>Browse 500+ Recipes</h3>
            <p>Filter by cuisine, diet, prep time, and more. Save your favourites for later.</p>
          </div>
          <div className="pl-step">
            <div className="pl-step-number">2</div>
            <h3>Build Your Weekly Menu</h3>
            <p>Drag meals into your weekly plan. Adjust servings and your shopping list updates automatically.</p>
          </div>
          <div className="pl-step">
            <div className="pl-step-number">3</div>
            <h3>Shop via Instacart</h3>
            <p>One tap sends your entire grocery list to Instacart for same-day delivery or pick-up.</p>
          </div>
        </div>
      </section>

      {/* ─── Features ─── */}
      <section className="pl-features">
        <h2 className="pl-features-title">Everything for Meal Planning</h2>
        <div className="pl-features-grid">
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-plan"><i className="fas fa-calendar-alt"></i></div>
            <h3>Smart Meal Plans</h3>
            <p>Generate a full week of meals tailored to your dietary preferences, or build your own from scratch.</p>
          </div>
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-list"><i className="fas fa-list-check"></i></div>
            <h3>Auto Shopping Lists</h3>
            <p>Ingredients are totalled across all meals. No duplicates, no guesswork — just a clean list.</p>
          </div>
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-cart"><i className="fas fa-cart-shopping"></i></div>
            <h3>Instacart Integration</h3>
            <p>Send your shopping list to Instacart with one click. Choose delivery or pick-up.</p>
          </div>
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-nutrition"><i className="fas fa-apple-whole"></i></div>
            <h3>Nutrition Tracking</h3>
            <p>Every recipe includes calorie and macro breakdowns so you can stay on top of your goals.</p>
          </div>
        </div>
      </section>

      {/* ─── Menu Showcase ─── */}
      <section className="pl-visual">
        <div className="pl-showcase-header">
          <span className="pl-showcase-count">500+</span>
          <span className="pl-showcase-label">Recipes Ready to Cook</span>
        </div>
        <div className="pl-showcase-grid">
          {SAMPLE_MENUS.map((menu, index) => (
            <Link
              key={menu.id}
              to={`/menu?search=${encodeURIComponent(menu.title)}`}
              className={`pl-showcase-card ${index === activeCardIndex ? 'active' : ''}`}
              aria-label={`View ${menu.title} on the menu`}
              onClick={() => trackCTAClick('meal_planning', 'showcase_card')}
            >
              <img
                src={menu.image}
                alt={menu.title}
                width="320"
                height="240"
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding="async"
                fetchPriority={index === 0 ? 'high' : 'auto'}
              />
              <div className="pl-showcase-overlay">
                <span className="pl-showcase-title">{menu.title}</span>
                <span className="pl-showcase-cuisine">{menu.cuisine}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="pl-final-cta">
        <h2>Ready to simplify your meal planning?</h2>
        <p>Join thousands who plan, cook, and shop smarter every week.</p>
        <div className="pl-final-cta-buttons">
          <button className="pl-cta-primary pl-cta-dark" onClick={() => { trackCTAClick('meal_planning', 'footer'); navigate('/menu'); }}>
            <span>Get Started Free</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
          <button className="pl-cta-secondary" onClick={() => { trackCTAClick('meal_planning', 'footer_pricing'); navigate('/pricing#pricing-personal'); }} style={{ borderColor: 'rgba(255,255,255,0.25)', color: '#fff', background: 'rgba(255,255,255,0.1)' }}>
            <span>See Plans &amp; Pricing</span>
          </button>
          <a href="https://apps.apple.com/us/app/freshmenulab/id6758483247" className="pl-app-badge pl-app-badge-dark" target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 384 512" width="18" height="18" fill="currentColor"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
            <div className="pl-badge-text"><span className="pl-badge-small">Download on the</span><span className="pl-badge-large">App Store</span></div>
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.freshmenulab" className="pl-app-badge pl-app-badge-dark" target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 512 512" width="18" height="18" fill="currentColor"><path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/></svg>
            <div className="pl-badge-text"><span className="pl-badge-small">Get it on</span><span className="pl-badge-large">Google Play</span></div>
          </a>
        </div>
      </section>

      <PersonaCrossSell current="meal_planning" />
    </div>
  );
};

export default MealPlanningLanding;
